import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { base44 } from '@/api/base44Client';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Leaf, Menu, ShoppingCart, User, Home, Store,
  LayoutDashboard, LogOut, Package, Settings, Truck, Shield,
  MapPin, Crown, UserCircle, Heart, Gift
} from 'lucide-react';
import Footer from '@/components/common/Footer';
import Logo from '@/components/common/Logo';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import { Helmet } from 'react-helmet-async';
import { getPageMetadata } from '@/utils';

import { useCart } from '@/contexts/CartContext';

// A lightweight, isolated component to prevent full Layout re-renders
const FlashingText = () => {
  const [showSub, setShowSub] = useState(true);
  useEffect(() => {
    const interval = setInterval(() => {
      setShowSub(prev => !prev);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative mr-2 font-bold tracking-wider text-xs uppercase hidden sm:block w-28 h-4 overflow-hidden text-white/90">
        <span className={`absolute inset-0 flex items-center transition-all duration-700 transform ${showSub ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>Subscribe Now</span>
        <span className={`absolute inset-0 flex items-center transition-all duration-700 transform ${!showSub ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}>Follow Us</span>
    </div>
  );
};

export default function Layout({ children, currentPageName, pageData = {} }) {
  const { t } = useTranslation();
  const location = useLocation();
  const meta = getPageMetadata(currentPageName, pageData);
  const canonicalUrl = `https://www.hamrokrishibazzar.com${location.pathname === '/' ? '' : location.pathname}`;
  const { cartCount } = useCart();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Removed local cartCount state
  const [userType, setUserType] = useState('buyer'); // 'admin', 'farmer', 'driver', 'buyer'
  const [farmerData, setFarmerData] = useState(null);
  const [driverData, setDriverData] = useState(null);
  const [reffCode, setReffCode] = useState(null);

  // Define loadUser outside useEffect to reusable
  const loadUser = async () => {
    setIsLoading(true);

    // 1. Check for local "Subscriber" session (for Footer Subscription Auto-Login)
    const subSession = localStorage.getItem('hamro_subscriber_session');
    if (subSession) {
      try {
        const subUser = JSON.parse(subSession);
        setUser(subUser);
        setUserType('buyer'); // Subscribers are treated as buyers
        setIsLoading(false);
        return; // Skip API call if locally subscribed
      } catch (e) {
        console.error("Invalid subscriber session", e);
        localStorage.removeItem('hamro_subscriber_session');
      }
    }

    // 2. Normal Backend Auth Check
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const currentUser = await base44.auth.me();
      if (!currentUser) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      setUser(currentUser);

      // Determine user type by priority: admin > farmer > driver > buyer
      if (currentUser.role === 'admin') {
        setUserType('admin');
      } else if (currentUser.role === 'marketing_agent') {
        setUserType('marketing_agent');
      } else {
        // Check if farmer
        const farmers = await base44.entities.Farmer.filter({ user_email: currentUser.email });
        if (farmers.length > 0 && farmers[0].subscription_status === 'verified') {
          setUserType('farmer');
          setFarmerData(farmers[0]);
          // Redirect farmer to dashboard if on home page
          if (window.location.pathname === '/' || window.location.pathname === createPageUrl('Home')) {
            window.location.href = createPageUrl('FarmerDashboard');
            return;
          }
        } else {
          // Check if driver
          const drivers = await base44.entities.Driver.filter({ user_email: currentUser.email });
          if (drivers.length > 0 && drivers[0].is_verified) {
            setUserType('driver');
            setDriverData(drivers[0]);
            // Redirect driver to dashboard if on home page
            if (window.location.pathname === '/' || window.location.pathname === createPageUrl('Home')) {
              window.location.href = createPageUrl('DriverDashboard');
              return;
            }
          } else {
            setUserType('buyer');
            // Redirect buyer to marketplace if on home page
            if (window.location.pathname === '/' || window.location.pathname === createPageUrl('Home')) {
              window.location.href = createPageUrl('Marketplace');
              return;
            }
          }
        }
      }
    } catch (e) {
      // Guest user or error
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // 1. Capture and save referral code from URL (Recursively search in nested params)
    const urlParams = new URLSearchParams(location.search);
    let ref = urlParams.get('ref');

    // Fail-safe: Check inside 'next' or 'from_url' if top-level ref is missing
    if (!ref) {
      const redirectUrl = urlParams.get('next') || urlParams.get('from_url');
      if (redirectUrl && redirectUrl.includes('ref=')) {
        try {
          // Try to treat as absolute or relative URL
          const nested = new URL(redirectUrl, window.location.origin);
          ref = nested.searchParams.get('ref');
        } catch (e) {
          const match = redirectUrl.match(/[?&]ref=([^&]+)/);
          if (match) ref = match[1];
        }
      }
    }

    if (ref) {
      console.log('✅ Referral link established:', ref);
      setReffCode(ref);
      // Save for 30 days
      const expires = new Date();
      expires.setDate(expires.getDate() + 30);
      document.cookie = `referral_code=${ref}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
    } else {
      // Try to recover from cookie on mount
      const cookieCode = document.cookie.split('; ').find(row => row.startsWith('referral_code='))?.split('=')[1];
      if (cookieCode) {
         setReffCode(cookieCode);
         console.log('⚓️ Referral recovered from cookie:', cookieCode);
      }
    }

    // Don't load user on callback page to prevent race condition 
    if (location.pathname.includes('/auth/google/callback') || location.pathname.includes('/oauth/verify')) {
      return;
    }

    loadUser();

    // Listen for subscriber login event from Footer
    window.addEventListener('subscriber-login', loadUser);
    return () => window.removeEventListener('subscriber-login', loadUser);
  }, [currentPageName, location.pathname]);

  const handleLogout = () => {
    // Clear local session too
    localStorage.removeItem('hamro_subscriber_session');
    base44.auth.logout(createPageUrl('Home'));
    setUser(null);
    window.location.reload(); // Force reload to clear state
  };

  // Conditional nav items based on user type
  const navItems = useMemo(() => {
    const items = [
      { name: t('nav.home'), page: 'Home', icon: Home },
    ];

    // Show Marketplace for buyers, farmers, marketing agents, and admins (not drivers)
    if (userType === 'buyer' || userType === 'farmer' || userType === 'admin' || userType === 'marketing_agent' || !user) {
      items.push({ name: t('nav.marketplace'), page: 'Marketplace', icon: Store });
    }

    items.push({ name: t('nav.help'), page: 'SupportCenter', icon: Settings });

    return items;
  }, [userType, user, t]);



  const getUserTypeLabel = () => {
    switch (userType) {
      case 'admin': return { label: 'Admin', color: 'bg-purple-100 text-purple-700' };
      case 'farmer': return { label: 'Farmer', color: 'bg-green-100 text-green-700' };
      case 'driver': return { label: 'Driver', color: 'bg-blue-100 text-blue-700' };
      case 'marketing_agent': return { label: 'Marketing Agent', color: 'bg-orange-100 text-orange-700' };
      default: return { label: 'Buyer', color: 'bg-gray-100 text-gray-700' };
    }
  };

  const typeInfo = getUserTypeLabel();

  // Don't show header/nav on landing page and auth pages, but still show footer
  const hideHeader =
    currentPageName === 'login' ||
    currentPageName === 'signup' ||
    currentPageName === 'forgot-password' ||
    currentPageName === 'reset-password';

  if (hideHeader) {

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content={meta.title} />
          <meta property="og:description" content={meta.description} />
          <meta property="og:url" content={canonicalUrl} />
          <meta name="twitter:title" content={meta.title} />
          <meta name="twitter:description" content={meta.description} />
        </Helmet>
        <main className="flex-grow">{children}</main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
      </Helmet>

      {/* ── TOP SOCIAL BAR (Ambika-style) ── */}
      <div className="text-white text-xs z-50 relative" style={{ backgroundColor: '#b91c1c' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-9">

            {/* Left: Brand-colored Social Icons */}
            <div className="flex items-center gap-2">
              <FlashingText />

              {/* Facebook — #1877F2 blue */}
              <a href="https://facebook.com/profile.php?id=61584566854466" target="_blank" rel="noopener noreferrer"
                title="Facebook"
                className="w-7 h-7 flex items-center justify-center rounded-full hover:scale-110 hover:brightness-110 transition-all duration-200 shadow-sm"
                style={{ backgroundColor: '#1877F2' }}>
                <svg viewBox="0 0 24 24" fill="white" className="w-3.5 h-3.5"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>

              {/* Instagram — gradient pink/purple */}
              <a href="https://instagram.com/hamrokrishibazzar/" target="_blank" rel="noopener noreferrer"
                title="Instagram"
                className="w-7 h-7 flex items-center justify-center rounded-full hover:scale-110 hover:brightness-110 transition-all duration-200 shadow-sm"
                style={{ background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fd5949 45%, #d6249f 60%, #285AEB 90%)' }}>
                <svg viewBox="0 0 24 24" fill="white" className="w-3.5 h-3.5"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
              </a>

              {/* TikTok — black */}
              <a href="https://www.tiktok.com/@kirshibazar" target="_blank" rel="noopener noreferrer"
                title="TikTok"
                className="w-7 h-7 flex items-center justify-center rounded-full hover:scale-110 hover:brightness-125 transition-all duration-200 shadow-sm"
                style={{ backgroundColor: '#010101' }}>
                <svg viewBox="0 0 24 24" fill="white" className="w-3.5 h-3.5"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
              </a>

              {/* X / Twitter — black */}
              <a href="https://x.com/govinsingh3406" target="_blank" rel="noopener noreferrer"
                title="X (Twitter)"
                className="w-7 h-7 flex items-center justify-center rounded-full hover:scale-110 hover:brightness-125 transition-all duration-200 shadow-sm"
                style={{ backgroundColor: '#000000' }}>
                <svg viewBox="0 0 24 24" fill="white" className="w-3.5 h-3.5"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg>
              </a>

              {/* YouTube — red */}
              <a href="https://youtube.com/@HamroKrishiBazzar?sub_confirmation=1" target="_blank" rel="noopener noreferrer"
                title="YouTube"
                className="w-7 h-7 flex items-center justify-center rounded-full hover:scale-110 hover:brightness-110 transition-all duration-200 shadow-sm"
                style={{ backgroundColor: '#FF0000' }}>
                <svg viewBox="0 0 24 24" fill="white" className="w-3.5 h-3.5"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>

            </div>

            {/* Center: Scrolling Announcement */}
            <div className="hidden sm:flex flex-1 items-center justify-center overflow-hidden mx-4">
              <div className="flex items-center gap-2 animate-marquee whitespace-nowrap">
                <span className="text-red-200">❖</span>
                <span className="font-semibold text-white/90">Fresh produce delivered daily from Nepali farms</span>
                <span className="text-red-200 mx-3">❖</span>
                <span className="font-semibold text-white/90">Free delivery on orders above रू 999</span>
                <span className="text-red-200 mx-3">❖</span>
                <span className="font-semibold text-white/90">100% verified farmers &amp; organic produce</span>
                <span className="text-red-200 mx-3">❖</span>
                <span className="font-semibold text-white/90">Now available on Google Play Store</span>
                <span className="text-red-200 mx-3">❖</span>
              </div>
            </div>

            {/* Right: WhatsApp transparent icon */}
            <a href="https://wa.me/9779849047275" target="_blank" rel="noopener noreferrer"
              title="WhatsApp Us"
              className="flex items-center justify-center hover:scale-110 transition-all duration-200">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                alt="WhatsApp"
                className="w-6 h-6 drop-shadow brightness-110"
              />
            </a>

          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={createPageUrl('Home')} className="flex items-center gap-2">
              <Logo className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold text-gray-900 hidden sm:block">{t('app_name')}</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item) => {
                const itemPath = createPageUrl(item.page);
                const isActive = itemPath === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(itemPath);

                return (
                  <Link
                    key={item.page}
                    to={itemPath}
                    className={`flex items-center gap-2 text-sm font-medium transition-colors ${isActive
                      ? 'text-green-600'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    <item.icon className={`w-4 h-4 ${isActive ? 'text-green-600' : ''}`} />
                    {item.name}
                  </Link>
                );
              })}

              {/* App Download Badges - Removed from here */}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Cart - show for buyers */}
              {userType !== 'driver' && (
                <Link to={createPageUrl('Cart')}>
                  <Button variant="ghost" size="icon" className="relative">
                    <ShoppingCart className="w-5 h-5" />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-600 text-white text-xs rounded-full flex items-center justify-center">
                        {cartCount}
                      </span>
                    )}
                  </Button>
                </Link>
              )}

              {/* Referral Link */}
              {user && (
                <Link to={createPageUrl('ReferralPage')}>
                  <Button variant="ghost" size="icon" className="relative">
                    <Gift className="w-5 h-5" />
                  </Button>
                </Link>
              )}

              {/* User Menu */}
              {!isLoading && (
                user ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex items-center gap-2 hover:bg-green-50 rounded-full pl-2 pr-4">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center border border-green-200">
                          <User className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="hidden sm:block text-sm font-semibold text-gray-700">
                          {user.full_name || user.email?.split('@')[0]}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64 p-2">
                      <div className="px-3 py-3 bg-gray-50/50 rounded-lg mb-2">
                        <p className="font-semibold text-gray-900">{user.full_name || user.email?.split('@')[0]}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        <Badge className={`mt-2 text-xs font-medium border-0 ${typeInfo.color}`}>
                          {typeInfo.label}
                        </Badge>
                      </div>

                      {/* ADMIN - Enhanced Menu */}
                      {userType === 'admin' && (
                        <>
                          <DropdownMenuLabel className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-2 mb-1">Admin Access</DropdownMenuLabel>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('AdminDashboard')} className="flex items-center gap-2 py-2.5">
                              <Shield className="w-4 h-4 text-purple-600" />
                              <span className="font-medium">Admin Panel</span>
                            </Link>
                          </DropdownMenuItem>
                          {/* Admin Delivery Management - assuming route exists or using placeholder */}
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('AdminDashboard') + '?tab=delivery'} className="flex items-center gap-2 py-2.5">
                              <Truck className="w-4 h-4 text-blue-600" />
                              <span className="font-medium">Delivery Management</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="my-1" />

                          <DropdownMenuLabel className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-2 mb-1">Role Management</DropdownMenuLabel>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('FarmerRegistration')} className="flex items-center gap-2 py-2.5">
                              <Store className="w-4 h-4 text-green-600" />
                              <span className="font-medium">Become a Seller/Farmer</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('DriverRegistration')} className="flex items-center gap-2 py-2.5">
                              <Truck className="w-4 h-4 text-blue-600" />
                              <span className="font-medium">Become a Driver</span>
                            </Link>
                          </DropdownMenuItem>
                          {/* If Admin wants to see dashboards directly, we can add them too, but registration is what was asked "Become a..." */}
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('FarmerDashboard')} className="flex items-center gap-2 py-2.5">
                              <LayoutDashboard className="w-4 h-4 text-green-600" />
                              <span className="font-medium">Farmer Dashboard (View)</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('DriverDashboard')} className="flex items-center gap-2 py-2.5">
                              <MapPin className="w-4 h-4 text-blue-600" />
                              <span className="font-medium">Driver Dashboard (View)</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="my-1" />

                          <DropdownMenuLabel className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-2 mb-1">Shopping & Tracking</DropdownMenuLabel>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('DeliveryTracking')} className="flex items-center gap-2 py-2.5">
                              <MapPin className="w-4 h-4 text-orange-500" />
                              <span className="font-medium">Track Delivery</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('BuyerOrders')} className="flex items-center gap-2 py-2.5">
                              <Package className="w-4 h-4 text-green-600" />
                              <span className="font-medium">My Orders</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="my-1" />
                        </>
                      )}

                      {/* FARMER - Sees Farmer Dashboard */}
                      {userType === 'farmer' && (
                        <>
                          <DropdownMenuLabel className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-2 mb-1">Farmer Tools</DropdownMenuLabel>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('FarmerDashboard')} className="flex items-center gap-2 py-2.5">
                              <LayoutDashboard className="w-4 h-4 text-green-600" />
                              <span className="font-medium">Farmer Dashboard</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('FarmerAnalytics')} className="flex items-center gap-2 py-2.5">
                              <Crown className="w-4 h-4 text-amber-500" />
                              <span className="font-medium">Analytics & Insights</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('FarmerSubscriptionPlans')} className="flex items-center gap-2 py-2.5">
                              <Settings className="w-4 h-4 text-gray-500" />
                              <span className="font-medium">Subscription Plans</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="my-1" />
                        </>
                      )}

                      {/* DRIVER - Sees Driver Dashboard */}
                      {userType === 'driver' && (
                        <>
                          <DropdownMenuLabel className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-2 mb-1">Driver Tools</DropdownMenuLabel>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('DriverDashboard')} className="flex items-center gap-2 py-2.5">
                              <Truck className="w-4 h-4 text-blue-600" />
                              <span className="font-medium">Driver Dashboard</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('DeliveryTracking')} className="flex items-center gap-2 py-2.5">
                              <MapPin className="w-4 h-4 text-blue-500" />
                              <span className="font-medium">My Deliveries</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="my-1" />
                        </>
                      )}

                      {/* MARKETING AGENT - Sees Agent Dashboard Link */}
                      {userType === 'marketing_agent' && (
                        <>
                          <DropdownMenuLabel className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-2 mb-1">Agent Tools</DropdownMenuLabel>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('ReferralPage')} className="flex items-center gap-2 py-2.5">
                              <Gift className="w-4 h-4 text-purple-600" />
                              <span className="font-medium">Marketing Dashboard</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="my-1" />
                        </>
                      )}

                      {/* BUYER Menu Items - For buyers, farmers, and agents (everyone can buy) */}
                      {(userType === 'buyer' || userType === 'farmer' || userType === 'marketing_agent') && (
                        <>
                          <DropdownMenuLabel className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-2 mb-1">Shopping</DropdownMenuLabel>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('BuyerOrders')} className="flex items-center gap-2 py-2.5">
                              <Package className="w-4 h-4 text-green-600" />
                              <span className="font-medium">My Orders</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('DeliveryTracking')} className="flex items-center gap-2 py-2.5">
                              <MapPin className="w-4 h-4 text-blue-500" />
                              <span className="font-medium">Track Delivery</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('Wishlist')} className="flex items-center gap-2 py-2.5">
                              <Heart className="w-4 h-4 text-red-500" />
                              <span className="font-medium">Wishlist</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('BuyerSubscriptions')} className="flex items-center gap-2 py-2.5">
                              <Crown className="w-4 h-4 text-amber-500" />
                              <span className="font-medium">Subscription Boxes</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('ReferralPage')} className="flex items-center gap-2 py-2.5">
                              <Gift className="w-4 h-4 text-purple-500" />
                              <span className="font-medium">Referral Program</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="my-1" />
                        </>
                      )}

                      {/* Become a Farmer/Seller/Driver - Only for buyers who aren't farmers */}
                      {userType === 'buyer' && (
                        <>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('FarmerRegistration')} className="flex items-center gap-2 py-2.5 text-emerald-700 bg-emerald-50/50 hover:bg-emerald-100/50">
                              <Leaf className="w-4 h-4" />
                              <span className="font-medium">Become a Farmer</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('FarmerRegistration')} className="flex items-center gap-2 py-2.5 text-green-700 bg-green-50/50 hover:bg-green-100/50">
                              <Store className="w-4 h-4" />
                              <span className="font-medium">Become a Seller</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={createPageUrl('DriverRegistration')} className="flex items-center gap-2 py-2.5 text-blue-700 bg-blue-50/50 hover:bg-blue-100/50">
                              <Truck className="w-4 h-4" />
                              <span className="font-medium">Become a Driver</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="my-1" />
                        </>
                      )}

                      <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50 focus:bg-red-50 py-2.5 mt-1">
                        <LogOut className="w-4 h-4 mr-2" />
                        <span className="font-medium">{t('nav.logout')}</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button
                    onClick={() => base44.auth.redirectToLogin()}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {t('nav.sign_in')}
                  </Button>
                )
              )}

              {/* Mobile Menu */}
              <Sheet>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72 bg-white sm:max-w-xs">
                  <div className="flex flex-col gap-4 mt-8">
                    {navItems.map((item) => (
                      <Link
                        key={item.page}
                        to={createPageUrl(item.page)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${currentPageName === item.page
                          ? 'bg-green-50 text-green-600'
                          : 'text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        <item.icon className="w-5 h-5 shrink-0" />
                        {item.name}
                      </Link>
                    ))}

                    {user && (
                      <>
                        <div className="border-t pt-4 mt-4">
                          {/* Role-based dashboard links */}
                          {userType === 'admin' && (
                            <Link
                              to={createPageUrl('AdminDashboard')}
                              className="flex items-center gap-3 px-4 py-3 rounded-xl text-purple-600 hover:bg-purple-50"
                            >
                              <Shield className="w-5 h-5 shrink-0" />
                              Admin Panel
                            </Link>
                          )}

                          {userType === 'farmer' && (
                            <>
                              <Link
                                to={createPageUrl('FarmerDashboard')}
                                className="flex items-center gap-3 px-4 py-3 rounded-xl text-green-600 hover:bg-green-50"
                              >
                                <LayoutDashboard className="w-5 h-5 shrink-0" />
                                Farmer Dashboard
                              </Link>
                              <Link
                                to={createPageUrl('FarmerAnalytics')}
                                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-50"
                              >
                                <Crown className="w-5 h-5 shrink-0" />
                                Analytics
                              </Link>
                            </>
                          )}

                          {userType === 'driver' && (
                            <Link
                              to={createPageUrl('DriverDashboard')}
                              className="flex items-center gap-3 px-4 py-3 rounded-xl text-blue-600 hover:bg-blue-50"
                            >
                              <Truck className="w-5 h-5 shrink-0" />
                              Driver Dashboard
                            </Link>
                          )}

                          {(userType === 'buyer' || userType === 'farmer' || userType === 'marketing_agent') && (
                            <>
                              <Link
                                to={createPageUrl('BuyerOrders')}
                                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-50"
                              >
                                <Package className="w-5 h-5 shrink-0" />
                                My Orders
                              </Link>
                              <Link
                                to={createPageUrl('DeliveryTracking')}
                                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-50"
                              >
                                <MapPin className="w-5 h-5 shrink-0" />
                                Track Delivery
                              </Link>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* App Download Banner Row */}
      <div className="text-white py-2 px-4 shadow-md border-b" style={{ backgroundColor: '#6B2D8B', borderColor: '#4A1A63' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-xs font-semibold tracking-wide hidden sm:flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full animate-pulse inline-block" style={{ backgroundColor: '#ef4444', boxShadow: '0 0 6px #ef4444' }}></span> App Available Now:
            </span>
            <div className="flex items-center gap-4">
              <a
                href={`https://play.google.com/store/apps/details?id=com.hamrokrishi.app${reffCode ? `&referrer=ref=${reffCode}` : ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-105 active:scale-95 transition-transform"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Get it on Google Play"
                  className="h-8 w-auto filter drop-shadow hover:brightness-110"
                />
              </a>
              
              <div className="hidden lg:flex items-center gap-2 group relative cursor-help">
                <span className="text-sm font-medium border-l border-purple-300 pl-4">Scan QR to Install</span>
                <div className="p-1 bg-white rounded shadow-sm group-hover:bg-purple-50 transition-colors">
                  <img
                    src="/hamro_app_playstore_qr.png"
                    alt="QR Code"
                    className="w-5 h-5 grayscale group-hover:grayscale-0"
                  />
                </div>
                
                {/* Fixed QR Hover Tooltip */}
                <div className="absolute top-10 left-0 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-300 p-4 bg-white rounded-2xl shadow-2xl border-2 z-[99] w-48" style={{ borderColor: '#6B2D8B' }}>
                  <div className="text-center">
                    <p className="text-xs text-slate-800 font-bold mb-2">Scan with your camera</p>
                    <img src="/hamro_app_playstore_qr.png" alt="Play Store QR" className="w-full h-auto rounded-lg" />
                    <div className="absolute top-[-8px] left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-t-2 border-l-2 rotate-45" style={{ borderColor: '#6B2D8B' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: '#F5C842', color: '#3B1F00' }}>
            <Leaf className="w-3 h-3" />
            Official HamroKrishi App
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <Footer />

      {/* WhatsApp Chatbot Floating Button */}
      <a
        href="https://wa.me/9779849047275"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-xl transition-all hover:scale-110 group animate-bounce-slow border border-gray-100"
        aria-label="Chat on WhatsApp"
      >
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
          alt="WhatsApp"
          className="w-10 h-10 drop-shadow-md"
        />
        <div className="absolute -top-10 right-0 bg-white text-gray-800 text-xs py-1 px-3 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap font-bold pointer-events-none">
          Questions? Chat with us!
          <div className="absolute bottom-[-4px] right-4 w-3 h-3 bg-white transform rotate-45"></div>
        </div>
      </a>
    </div>
  );
}
